import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faCheck } from "@fortawesome/free-solid-svg-icons"
import {
	postTypeTacheVehicule,
	postTypeTacheVehiculeReset,
	deleteTypeTacheVehicule,
	deleteTypeTacheVehiculeReset,
} from "../../../store/actions/autreActions"
import { PROPRE_VEHICULE_TYPE_CHOICES } from "../../../utilities/Addons"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import CustomModal from "../../basics/interface/CustomModal"

// Portées (miroir de TypeTacheVehicule côté backend).
const PORTEE_FLOTTE = 0
const PORTEE_TYPE = 1
const PORTEE_VEHICULE = 2
const PORTEE_CHOICES = [
	[PORTEE_FLOTTE, "Tous les véhicules (flotte)"],
	[PORTEE_TYPE, "Par type de véhicule"],
	[PORTEE_VEHICULE, "Par véhicule précis"],
]

const checkRow = {
	display: "flex",
	alignItems: "center",
	gap: 8,
	padding: "6px 4px",
	fontSize: 13,
	cursor: "pointer",
}

// Édition d'une tâche véhicule (catalogue). POST attend un objet
// {id, nom, ordre, portee, portee_types[], portee_vehicules[]} ; POST/DELETE
// renvoient la liste à jour → reducer (autreInfo.typesTacheVehicule).
const TacheVehiculeModalV2 = ({ tache, onClose }) => {
	const dispatch = useDispatch()
	const postState = useSelector((s) => s.autreInfo.postTypeTV)
	const deleteState = useSelector((s) => s.autreInfo.deleteTypeTV)
	const masterVehicules = useSelector((s) => s.masterReducer.masterVehicules)
	const isCreate = !tache.id

	const [confirmDelete, setConfirmDelete] = useState(false)
	const [nom, setNom] = useState(tache.nom || "")
	const [ordre, setOrdre] = useState(tache.ordre != null ? tache.ordre : 0)
	const [portee, setPortee] = useState(
		tache.portee != null ? tache.portee : PORTEE_FLOTTE,
	)
	const [types, setTypes] = useState(tache.portee_types || [])
	const [vehs, setVehs] = useState(tache.portee_vehicules || [])

	// Flotte = véhicules ayant un PropreVehicule ; valeur = id du PropreVehicule.
	const flotte = (masterVehicules || [])
		.filter((v) => v.propre_vehicule)
		.map((v) => ({
			pvId: v.propre_vehicule.id,
			label: `${v.marque || ""} ${v.modele || ""} ${v.plaques || ""}`.trim(),
		}))

	useEffect(() => {
		if (!postState.saved && !deleteState.deleted) return
		const t = setTimeout(() => {
			dispatch(postTypeTacheVehiculeReset())
			dispatch(deleteTypeTacheVehiculeReset())
			onClose()
		}, 800)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postState.saved, deleteState.deleted])

	useEffect(
		() => () => {
			dispatch(postTypeTacheVehiculeReset())
			dispatch(deleteTypeTacheVehiculeReset())
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	useEffect(() => {
		if (deleteState.error) setConfirmDelete(false)
	}, [deleteState.error])

	const toggle = (list, setList, value) =>
		setList(
			list.includes(value)
				? list.filter((v) => v !== value)
				: [...list, value],
		)

	const invalid =
		!nom.trim() ||
		(portee === PORTEE_TYPE && types.length === 0) ||
		(portee === PORTEE_VEHICULE && vehs.length === 0)

	const save = () => {
		const payload = {
			id: tache.id || 0,
			nom: nom.trim(),
			ordre: Number(ordre) || 0,
			portee,
			portee_types: portee === PORTEE_TYPE ? types : [],
			portee_vehicules: portee === PORTEE_VEHICULE ? vehs : [],
		}
		dispatch(postTypeTacheVehicule(JSON.stringify(payload)))
	}
	const remove = () => {
		if (!confirmDelete) {
			setConfirmDelete(true)
			return
		}
		dispatch(deleteTypeTacheVehicule(JSON.stringify({ id: tache.id })))
	}

	const busy =
		postState.saving ||
		postState.saved ||
		deleteState.deleting ||
		deleteState.deleted

	const buttons = []
	if (!isCreate) {
		buttons.push({
			title: deleteState.deleting ? (
				<FontAwesomeIcon icon={faSync} spin />
			) : confirmDelete ? (
				"Confirmer ?"
			) : (
				"Supprimer"
			),
			styleName: "danger",
			onClick: remove,
			disabled: busy,
		})
	}
	buttons.push({
		title: "Annuler",
		styleName: "secondary",
		onClick: onClose,
		disabled: busy,
	})
	buttons.push({
		title: postState.saving ? (
			<FontAwesomeIcon icon={faSync} spin />
		) : postState.saved ? (
			<FontAwesomeIcon icon={faCheck} />
		) : (
			"Enregistrer"
		),
		styleName: "primary",
		onClick: save,
		disabled: busy || invalid,
	})

	return (
		<CustomModal
			title={isCreate ? "Nouvelle tâche véhicule" : "Éditer la tâche"}
			onClose={busy ? () => {} : onClose}
			showCloseButton={!busy}
			buttons={buttons}
		>
			<div style={gs.fieldGroup}>
				<div style={gs.fieldRow}>
					<label style={gs.label}>
						Nom<span style={{ color: COLORS.danger }}> *</span>
					</label>
					<input
						style={gs.input}
						value={nom}
						onChange={(e) => setNom(e.target.value)}
						autoFocus
					/>
				</div>
				<div style={gs.fieldRow}>
					<label style={gs.label}>Ordre</label>
					<input
						style={gs.input}
						type="number"
						value={ordre}
						onChange={(e) => setOrdre(e.target.value)}
					/>
				</div>
				<div style={gs.fieldRow}>
					<label style={gs.label}>Appliquer à</label>
					<select
						style={gs.input}
						value={portee}
						onChange={(e) => setPortee(Number(e.target.value))}
					>
						{PORTEE_CHOICES.map(([val, lib]) => (
							<option key={val} value={val}>
								{lib}
							</option>
						))}
					</select>
				</div>

				{portee === PORTEE_TYPE && (
					<div style={gs.fieldRow}>
						<label style={gs.label}>Types concernés</label>
						<div>
							{PROPRE_VEHICULE_TYPE_CHOICES.map(([val, lib]) => (
								<label key={val} style={checkRow}>
									<input
										type="checkbox"
										checked={types.includes(val)}
										onChange={() => toggle(types, setTypes, val)}
									/>
									{lib}
								</label>
							))}
						</div>
					</div>
				)}

				{portee === PORTEE_VEHICULE && (
					<div style={gs.fieldRow}>
						<label style={gs.label}>Véhicules concernés</label>
						<div style={{ maxHeight: 220, overflowY: "auto" }}>
							{flotte.length === 0 ? (
								<span style={{ fontSize: 13, color: COLORS.textLight }}>
									Aucun véhicule dans la flotte.
								</span>
							) : (
								flotte.map((v) => (
									<label key={v.pvId} style={checkRow}>
										<input
											type="checkbox"
											checked={vehs.includes(v.pvId)}
											onChange={() =>
												toggle(vehs, setVehs, v.pvId)
											}
										/>
										{v.label || "—"}
									</label>
								))
							)}
						</div>
					</div>
				)}
			</div>
		</CustomModal>
	)
}

export default TacheVehiculeModalV2
